/*
 * ॐ नमः शिवाय
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * HtmlCssCardsPanel — the button and badge shown on each card of a data-bound
 * list, without the admin editing the section's JavaScript.
 *
 * Named for the card, not the button, because it owns the badge too. WHICH
 * ROWS reach the card is a different question and lives with the binding that
 * fetches them — see HtmlCssDataBindingsPanel. Row filtering cannot live here:
 * the row limit is applied by the server, so hiding rows after the fetch would
 * leave the grid short.
 *
 * Settings hang off a data binding's div_id, not off the page, so a section
 * binding two lists configures each one separately. A binding with no div_id
 * therefore cannot be configured at all — there is nothing to attach the
 * settings to — and the panel says so rather than silently offering controls
 * whose values could never be read back.
 *
 * The value lists for the rule and the badge come from the bound table's own
 * fields: only fields with a fixed set of options can drive either, so adding
 * an option to the table later surfaces it here instead of rendering a blank
 * badge on the card.
 *
 * Shared by HtmlCssWorkspaceModal (full-screen tab) and HtmlCssEditor (compact
 * sidebar tab), mirroring how HtmlCssDataBindingsPanel is shared.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiHelper } from '../../hooks/usePages';

const labelStyle = { fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 3 };
const inputStyle = {
  width: '100%',
  padding: '5px 8px',
  fontSize: 12,
  border: '1px solid #e2e8f0',
  borderRadius: 5,
  outline: 'none',
  background: '#fff',
};
const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: '#94a3b8',
  margin: '4px 0 2px',
};

export const STYLE_CHOICES = [
  { key: 'text',     label: 'Text link' },
  { key: 'outlined', label: 'Outlined'  },
  { key: 'filled',   label: 'Filled'    },
];

export const OPERATORS = [
  { key: 'any', label: 'any value',  multi: false, needsValue: false },
  { key: 'eq',  label: 'is',         multi: false, needsValue: true  },
  { key: 'neq', label: 'is not',     multi: false, needsValue: true  },
  { key: 'in',  label: 'is one of',  multi: true,  needsValue: true  },
];

export const TONES = [
  { key: 'open',     label: 'Open',     color: '#0B6B4A' },
  { key: 'warn',     label: 'Closing',  color: '#8A5A12' },
  { key: 'critical', label: 'Critical', color: '#A03226' },
  { key: 'info',     label: 'Info',     color: '#3C5580' },
  { key: 'muted',    label: 'Muted',    color: '#6B7A73' },
];

/**
 * Reads a field's fixed option list.
 *
 * Two storage shapes exist in the wild and both have to work: a plain CSV
 * ("a,b,c") written through the table editor, and a Python list repr
 * ("['A', 'B']") written by the application packs. Splitting on commas alone
 * turns the second into `['A'` and `'B']`, which is exactly the garbage a
 * builder would otherwise be asked to label.
 */
export function parseAllowedOptions(raw) {
  const text = String(raw || '').trim();
  if (!text) return [];
  return text
    .replace(/^\[|\]$/g, '')
    .split(',')
    .map((part) => part.trim().replace(/^['"]|['"]$/g, '').trim())
    .filter(Boolean);
}

/** The fields of `table` that can drive a rule or a badge, i.e. those with options. */
export function optionFieldsOf(table) {
  return (table?.columns || [])
    .map((c) => ({ ...c, options: parseAllowedOptions(c.allowed_options) }))
    .filter((c) => c.options.length > 0);
}

function Empty({ children }) {
  return (
    <div
      style={{
        padding: 14,
        border: '1px dashed #e2e8f0',
        borderRadius: 6,
        background: '#f8fafc',
        color: '#64748b',
        fontSize: 12,
        lineHeight: 1.6,
      }}
    >
      {children}
    </div>
  );
}

function Chip({ on, label, onToggle }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onToggle}
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 9px',
        borderRadius: 999,
        cursor: 'pointer',
        border: `1px solid ${on ? 'var(--primary-color, #4e2e80)' : '#e2e8f0'}`,
        background: on ? 'var(--primary-color, #4e2e80)' : '#fff',
        color: on ? '#fff' : '#64748b',
      }}
    >
      {label}
    </button>
  );
}

/** One binding's card settings. */
function BindingCard({ binding, table, entry, onChange }) {
  const badge = entry.badge || {};
  // A list, because one card wants different words in different states —
  // "Register" while registration is open, "View details" once it is not.
  const buttons = entry.buttons || (entry.button ? [entry.button] : []);

  const optionFields = optionFieldsOf(table);
  const badgeField = optionFields.find((f) => f.name === badge.field);

  const setButtons = (next) => onChange({ ...entry, buttons: next, button: undefined });
  const patchButton = (index, patch) =>
    setButtons(buttons.map((b, i) => (i === index ? { ...b, ...patch } : b)));
  const patchRule = (index, patch) => {
    const rule = buttons[index].visible_when || { field: '', op: 'any', values: [] };
    patchButton(index, { visible_when: { ...rule, ...patch } });
  };
  const patchBadge = (patch) => onChange({ ...entry, badge: { ...badge, ...patch } });

  const addButton = () =>
    setButtons([...buttons, { label: '', icon: '', style: 'text',
      visible_when: { field: '', op: 'any', values: [] } }]);
  const removeButton = (index) => setButtons(buttons.filter((_, i) => i !== index));

  return (
    <div
      style={{
        padding: 12,
        marginBottom: 10,
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{binding.div_id}</span>
        <span style={{ fontSize: 11, color: '#94a3b8' }}>
          {table?.display_name || table?.name || binding.table_slug}
        </span>
      </div>

      {!table ? (
        <Empty>
          This binding&apos;s table could not be read, so there are no fields to offer. Check the table in the
          Data Bindings tab.
        </Empty>
      ) : (
        <>
          <div style={sectionLabelStyle}>Buttons</div>

          {buttons.length === 0 ? (
            <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 8px' }}>
              No buttons on these cards yet.
            </p>
          ) : null}

          {buttons.map((button, bi) => {
            const rule = button.visible_when || { field: '', op: 'any', values: [] };
            const ruleField = optionFields.find((f) => f.name === rule.field);
            const operator = OPERATORS.find((o) => o.key === (rule.op || 'any')) || OPERATORS[0];
            const label = `${binding.div_id} button ${bi + 1}`;

            // Narrowing from "is one of" to a single-value operator keeps the
            // first choice rather than discarding the whole selection.
            const changeOperator = (key) => {
              const next = OPERATORS.find((o) => o.key === key);
              const values = next.multi ? (rule.values || []) : (rule.values || []).slice(0, 1);
              patchRule(bi, { op: key, values });
            };
            const toggleValue = (value) => {
              const current = rule.values || [];
              const at = current.indexOf(value);
              patchRule(bi, {
                values: at === -1 ? [...current, value] : current.filter((v) => v !== value),
              });
            };

            return (
              <div
                key={bi}
                style={{
                  border: '1px solid #eef2f7', borderRadius: 6,
                  padding: 10, marginBottom: 8, background: '#fbfdff',
                }}
              >
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <div style={{ flex: 1.5 }}>
                    <div style={labelStyle}>Button text</div>
                    <input
                      type="text"
                      style={inputStyle}
                      maxLength={40}
                      aria-label={`Label for ${label}`}
                      value={button.label || ''}
                      placeholder="leave empty for no button"
                      onChange={(e) => patchButton(bi, { label: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={labelStyle}>Style</div>
                    <select
                      style={inputStyle}
                      aria-label={`Style for ${label}`}
                      value={button.style || 'text'}
                      onChange={(e) => patchButton(bi, { style: e.target.value })}
                    >
                      {STYLE_CHOICES.map((s) => (
                        <option key={s.key} value={s.key}>{s.label}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    type="button"
                    aria-label={`Remove ${label}`}
                    onClick={() => removeButton(bi)}
                    style={{
                      alignSelf: 'flex-end', border: '1px solid #fecaca', borderRadius: 5,
                      background: '#fef2f2', color: '#dc2626', cursor: 'pointer',
                      fontSize: 12, padding: '5px 9px',
                    }}
                  >
                    ✕
                  </button>
                </div>

                <div style={{ marginBottom: 8 }}>
                  <div style={labelStyle}>Icon (optional)</div>
                  <input
                    type="text"
                    style={inputStyle}
                    maxLength={24}
                    aria-label={`Icon for ${label}`}
                    value={button.icon || ''}
                    placeholder="a Bootstrap icon name, e.g. bi-arrow-right"
                    onChange={(e) => patchButton(bi, { icon: e.target.value })}
                  />
                </div>

                <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={labelStyle}>Show it only when</div>
                    <select
                      style={inputStyle}
                      aria-label={`Rule field for ${label}`}
                      value={rule.field || ''}
                      onChange={(e) => patchRule(bi, { field: e.target.value, values: [] })}
                    >
                      <option value="">always show</option>
                      {optionFields.map((f) => (
                        <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
                      ))}
                    </select>
                  </div>
                  {rule.field ? (
                    <div style={{ flex: 1 }}>
                      <div style={labelStyle}>Comparison</div>
                      <select
                        style={inputStyle}
                        aria-label={`Rule comparison for ${label}`}
                        value={rule.op || 'any'}
                        onChange={(e) => changeOperator(e.target.value)}
                      >
                        {OPERATORS.map((o) => (
                          <option key={o.key} value={o.key}>{o.label}</option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>

                {rule.field && operator.needsValue ? (
                  operator.multi ? (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {(ruleField?.options || []).map((v) => (
                        <Chip
                          key={v}
                          label={v}
                          on={(rule.values || []).indexOf(v) !== -1}
                          onToggle={() => toggleValue(v)}
                        />
                      ))}
                    </div>
                  ) : (
                    <select
                      style={inputStyle}
                      aria-label={`Rule value for ${label}`}
                      value={(rule.values || [])[0] || ''}
                      onChange={(e) => patchRule(bi, { values: [e.target.value] })}
                    >
                      <option value="">choose a value</option>
                      {(ruleField?.options || []).map((v) => (
                        <option key={v} value={v}>{v}</option>
                      ))}
                    </select>
                  )
                ) : null}
              </div>
            );
          })}

          <button
            type="button"
            onClick={addButton}
            style={{
              width: '100%', padding: 8, marginBottom: 10, cursor: 'pointer',
              border: '1px dashed #cbd5e1', borderRadius: 6,
              background: '#f8fafc', color: '#475569', fontSize: 12, fontWeight: 600,
            }}
          >
            + Add a button
          </button>

          <div style={sectionLabelStyle}>Badge</div>

          <div style={{ marginBottom: 8 }}>
            <div style={labelStyle}>Shows the value of</div>
            <select
              style={inputStyle}
              aria-label={`Badge field for ${binding.div_id}`}
              value={badge.field || ''}
              onChange={(e) => patchBadge({ field: e.target.value })}
            >
              <option value="">no badge</option>
              {optionFields.map((f) => (
                <option key={f.name} value={f.name}>{f.display_name || f.name}</option>
              ))}
            </select>
          </div>

          {badgeField ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {badgeField.options.map((v) => (
                <div key={v} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span
                    style={{
                      flex: '0 0 34%',
                      fontSize: 10.5,
                      fontFamily: 'monospace',
                      color: '#64748b',
                      background: '#f1f5f9',
                      padding: '5px 7px',
                      borderRadius: 4,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {v}
                  </span>
                  <input
                    type="text"
                    style={{ ...inputStyle, flex: 1 }}
                    maxLength={30}
                    aria-label={`Badge label for ${v}`}
                    placeholder="not shown"
                    value={(badge.labels || {})[v] || ''}
                    onChange={(e) =>
                      patchBadge({ labels: { ...(badge.labels || {}), [v]: e.target.value } })
                    }
                  />
                  <select
                    style={{ ...inputStyle, flex: '0 0 92px' }}
                    aria-label={`Badge colour for ${v}`}
                    value={(badge.colors || {})[v] || 'muted'}
                    onChange={(e) =>
                      patchBadge({ colors: { ...(badge.colors || {}), [v]: e.target.value } })
                    }
                  >
                    {TONES.map((t) => (
                      <option key={t.key} value={t.key}>{t.label}</option>
                    ))}
                  </select>
                </div>
              ))}
              <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', lineHeight: 1.5 }}>
                A value left empty shows no badge on the card.
              </p>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default function HtmlCssCardsPanel({ appId, bindings, cardButtons, onChange }) {
  const rows = bindings || [];
  const configured = cardButtons || [];

  const { data: tablesData } = useQuery({
    queryKey: ['tableList', appId, 'withFields'],
    queryFn: async ({ signal }) => {
      const resp = await apiHelper.apiCall(
        `/arcee/application/table/list/?application_id=${appId}&page_size=200&include_fields=1`,
        { method: 'GET', signal }
      );
      if (!resp.ok) return { tables: [] };
      return resp.json();
    },
    enabled: !!appId,
    staleTime: 60_000,
  });

  const tables = tablesData?.tables || tablesData?.results || [];

  // Only a named binding can carry settings — the div_id is the key they are
  // stored under, and the key the sandbox looks them up by at render time.
  const addressable = rows.filter((b) => b.div_id && b.table_slug);

  const entryFor = (divId) =>
    configured.find((c) => c.div_id === divId) || { div_id: divId, button: null, badge: null };

  const updateEntry = (divId, next) => {
    const without = configured.filter((c) => c.div_id !== divId);
    onChange([...without, next]);
  };

  if (rows.length === 0) {
    return (
      <Empty>
        Card buttons apply to a list rendered from a public table. Add a data binding in the{' '}
        <strong>Data Bindings</strong> tab and it will appear here.
      </Empty>
    );
  }

  if (addressable.length === 0) {
    return (
      <Empty>
        None of this section&apos;s bindings has a <strong>Div ID</strong>. The id is what these settings are
        stored under, so give each binding one in the <strong>Data Bindings</strong> tab first.
      </Empty>
    );
  }

  return (
    <div>
      {addressable.map((binding) => (
        <BindingCard
          key={binding.div_id}
          binding={binding}
          table={tables.find((t) => t.name === binding.table_slug)}
          entry={entryFor(binding.div_id)}
          onChange={(next) => updateEntry(binding.div_id, next)}
        />
      ))}
      <p style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 0', lineHeight: 1.6 }}>
        Where the button goes is set in the <strong>Links</strong> tab, which already routes the card itself.
      </p>
    </div>
  );
}
